import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PageHeader, Panel, PanelHeader } from '../components/admin';

const SECTIONS = [
  { id: 'general', label: 'General' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'video', label: 'Video' },
  { id: 'privacy', label: 'Privacy' },
];

function Toggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex items-center justify-between w-full py-1 group"
      role="switch"
      aria-checked={checked}
    >
      <span className="text-sm text-gray-300">{label}</span>
      <span
        className={`relative w-10 h-5.5 rounded-full transition-colors ${
          checked ? 'bg-accent' : 'bg-surface-600 border border-line-strong'
        }`}
      >
        <span
          className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white transition-transform ${
            checked ? 'translate-x-[19px]' : 'translate-x-0.5'
          }`}
        />
      </span>
    </button>
  );
}

function Settings() {
  const [activeSection, setActiveSection] = useState('general');
  const [settings, setSettings] = useState({
    theme: 'dark',
    notifications: {
      email: true,
      push: true,
      newsletter: false,
    },
    language: 'en',
    videoQuality: 'auto',
    privacy: {
      profileVisibility: 'public',
      showEmail: false,
      showPhone: false,
    },
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/user-settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (response.data) setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleSettingChange = async (category, key, value) => {
    try {
      const updatedSettings = {
        ...settings,
        [category]: {
          ...settings[category],
          [key]: value,
        },
      };
      await axios.put(`${process.env.REACT_APP_API_URL}/user-settings`, updatedSettings, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setSettings(updatedSettings);
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const handleThemeChange = async (theme) => {
    try {
      const updatedSettings = { ...settings, theme };
      await axios.put(`${process.env.REACT_APP_API_URL}/user-settings`, updatedSettings, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setSettings(updatedSettings);
      document.documentElement.classList.toggle('dark', theme === 'dark');
    } catch (error) {
      console.error('Error updating theme:', error);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Settings" description="Workspace preferences for your admin account" />

      <div className="flex flex-col lg:flex-row gap-5">
        {/* Section nav */}
        <Panel padded={false} className="lg:w-60 shrink-0 self-start">
          <nav className="p-2">
            {SECTIONS.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveSection(section.id)}
                className={`w-full text-left rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  activeSection === section.id
                    ? 'bg-accent-dim text-accent-soft'
                    : 'text-gray-400 hover:text-white hover:bg-surface-700'
                }`}
              >
                {section.label}
              </button>
            ))}
          </nav>
        </Panel>

        {/* Section content */}
        <Panel className="flex-1">
          <PanelHeader
            title={SECTIONS.find((s) => s.id === activeSection)?.label}
            description="Changes are saved automatically"
          />
          <div className="p-5 space-y-5">
            {activeSection === 'general' && (
              <>
                <div>
                  <label className="field-label">Theme</label>
                  <select
                    value={settings.theme}
                    onChange={(e) => handleThemeChange(e.target.value)}
                    className="field max-w-xs"
                  >
                    <option value="dark">Dark</option>
                    <option value="light">Light</option>
                  </select>
                </div>
                <div>
                  <label className="field-label">Language</label>
                  <select
                    value={settings.language}
                    onChange={(e) => handleSettingChange('language', 'language', e.target.value)}
                    className="field max-w-xs"
                  >
                    <option value="en">English</option>
                    <option value="fr">French</option>
                    <option value="es">Spanish</option>
                  </select>
                </div>
              </>
            )}

            {activeSection === 'notifications' && (
              <div className="divide-y divide-line">
                <div className="pb-3">
                  <Toggle
                    label="Email notifications"
                    checked={settings.notifications.email}
                    onChange={(v) => handleSettingChange('notifications', 'email', v)}
                  />
                </div>
                <div className="py-3">
                  <Toggle
                    label="Push notifications"
                    checked={settings.notifications.push}
                    onChange={(v) => handleSettingChange('notifications', 'push', v)}
                  />
                </div>
                <div className="pt-3">
                  <Toggle
                    label="Newsletter"
                    checked={settings.notifications.newsletter}
                    onChange={(v) => handleSettingChange('notifications', 'newsletter', v)}
                  />
                </div>
              </div>
            )}

            {activeSection === 'video' && (
              <div>
                <label className="field-label">Default video quality</label>
                <select
                  value={settings.videoQuality}
                  onChange={(e) => handleSettingChange('videoQuality', 'videoQuality', e.target.value)}
                  className="field max-w-xs"
                >
                  <option value="auto">Auto</option>
                  <option value="1080p">1080p</option>
                  <option value="720p">720p</option>
                  <option value="480p">480p</option>
                  <option value="360p">360p</option>
                </select>
              </div>
            )}

            {activeSection === 'privacy' && (
              <>
                <div>
                  <label className="field-label">Profile visibility</label>
                  <select
                    value={settings.privacy.profileVisibility}
                    onChange={(e) => handleSettingChange('privacy', 'profileVisibility', e.target.value)}
                    className="field max-w-xs"
                  >
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                    <option value="friends">Friends only</option>
                  </select>
                </div>
                <div className="divide-y divide-line">
                  <div className="pb-3">
                    <Toggle
                      label="Show email address"
                      checked={settings.privacy.showEmail}
                      onChange={(v) => handleSettingChange('privacy', 'showEmail', v)}
                    />
                  </div>
                  <div className="pt-3">
                    <Toggle
                      label="Show phone number"
                      checked={settings.privacy.showPhone}
                      onChange={(v) => handleSettingChange('privacy', 'showPhone', v)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}

export default Settings;
